import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import api from '@/api';
import { nextArchivePage, PAGE_SIZE } from '@/archive/PostArchive';
import { TermArchive } from '@/archive/TermArchive';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

/**
 * `/news/category/$slug`, the student news category archive.
 *
 * Static segments rank above dynamic ones, so `/news/category/announcements`
 * resolves here, not to `news.$slug`. `/news/category` alone does fall to
 * `news.$slug`; nothing links there.
 */
function StudentNewsTermArchive(): ReactElement | null {
  const { slug } = Route.useParams();
  // The interface language, which is part of the query key. Unlike legacy
  // there's no language prefix in the URL.
  const language = localeToApiLanguage(apiLanguage());

  const query = api.listStudentNews.useInfiniteQuery(
    { query: { language, category: slug, limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // Wrapped rather than passed by reference: an annotated `lastPage`
      // would be inferred as the operation's `TQueryFnData` and collapse the
      // page type to the helper's structural one.
      getNextPageParam: (lastPage, allPages) =>
        nextArchivePage(lastPage, allPages),
    },
  );

  return (
    <TermArchive
      query={query}
      taxonomy="student_news_category"
      slug={slug}
      headingKey="wbs.student-news"
      basePath="/news"
      emptyTestId="student-news-empty"
      sidebarKey="student-news-sidebar"
      categoryTo="/news/category/$slug"
      language={language}
    />
  );
}

export const Route = createFileRoute('/_app/news/category/$slug')({
  component: StudentNewsTermArchive,
});
